import type { Rival } from '../contract';
import { CheckIcon } from 'lucide-react';
import { initials } from '../lib/oura';

/**
 * Not a leaderboard — a leaderboard tells an owner they are losing. This tells
 * them what to do on Monday. The gap is sourced from `whatTheyHave` and the
 * questions this rival wins, never from a total score compare (§Who wins).
 */
export function HeadToHead({
  you,
  rival,
  questionText,
}: {
  you: string;
  rival: Rival;
  questionText: (id: string) => string;
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-4 flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-600 text-sm font-bold text-white">
          {initials(you)}
        </span>
        <span className="text-sm font-medium text-gray-400">vs</span>
        <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-400 text-sm font-bold text-white">
          {initials(rival.name)}
        </span>
        <div className="ml-1">
          <p className="text-sm font-semibold text-gray-900">{rival.name}</p>
          <p className="text-xs text-gray-500">{rival.domain}</p>
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 bg-gray-50 p-3.5">
        <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">
          What they have that you don't
        </p>
        <p className="mt-1 text-sm text-gray-900">{rival.whatTheyHave}</p>
      </div>

      {rival.winsQuestionIds.length > 0 ? (
        <div className="mt-4">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
            Questions they win and you don't
          </p>
          <ul className="space-y-1.5">
            {rival.winsQuestionIds.map((id) => (
              <li key={id} className="flex items-center gap-2 text-sm text-gray-700">
                <CheckIcon className="h-4 w-4 flex-shrink-0 text-gray-400" />
                <span className="italic">"{questionText(id)}"</span>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p className="mt-4 text-sm text-gray-500">
          You are ahead of them on every question we tested.
        </p>
      )}
    </div>
  );
}
